import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';

import {
  usePandaDocProvisioningFoldersQuery,
  ProvisioningFolder,
  ProvisioningStudentDoc,
} from 'core/graphql';

import { isPermissionDeniedError } from './usePandaDocAnalytics';

export type ProvisioningFolderRow = ProvisioningFolder;
export type ProvisioningStudentDocRow = ProvisioningStudentDoc;

interface IProvisioningFoldersHook {
  folders: ProvisioningFolderRow[];
  loading: boolean;
  /** Stays true on every refetch so the screen can dim stale data. */
  fetching: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

/**
 * Loads PandaDoc folders of signed student contracts ready for provisioning.
 * `course` optionally narrows the list to one classified course.
 */
export const useProvisioningFolders = (
  course?: string,
): IProvisioningFoldersHook => {
  const {
    data,
    loading,
    error,
    refetch,
    networkStatus,
  } = usePandaDocProvisioningFoldersQuery({
    variables: { course: course || undefined },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Stable reference — consumers list it in effect dependency arrays.
  const stableRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    folders: (
      data?.pandadocProvisioningFolders ?? []
    ).filter((folder): folder is ProvisioningFolderRow => Boolean(folder)),
    loading,
    // networkStatus 1 = initial loading, 4 = refetch, 2 = setVariables.
    fetching: loading || networkStatus === 4 || networkStatus === 2,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch: stableRefetch,
  };
};
